import { useState } from 'react'
import { reverseGeocode } from '../lib/geocode'

export interface GeolocateResult {
  lat: number
  lng: number
  label: string | null
}

/**
 * On-demand "use my current location" trigger for the discover search boxes.
 * Unlike useCurrentPosition (which auto-fires once on mount to seed the initial
 * center), this returns a `locate` callback wired to a clickable pin: it reads
 * GPS, reverse-geocodes a human-readable label, and hands both back to the
 * caller. `locating` reflects the in-flight state so the pin can be disabled.
 */
export function useGeolocate() {
  const [locating, setLocating] = useState(false)

  function locate(onLocated: (result: GeolocateResult) => void) {
    if (!navigator.geolocation) return
    setLocating(true)
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords
        const label = await reverseGeocode(latitude, longitude)
        onLocated({ lat: latitude, lng: longitude, label })
        setLocating(false)
      },
      () => setLocating(false),
      { timeout: 8000 },
    )
  }

  return { locating, locate }
}
